<template>
  <span
    ref="rootRef"
    class="inline-date"
    :class="{ 'inline-date--compact': compact, 'inline-date--bordered': bordered }"
    @mousedown="onFieldMouseDown"
  >
    <span
      v-if="bordered && label"
      class="inline-date__label"
    >{{ label }}</span>
    <app-icon
      v-if="showCalendar && editable"
      class="inline-date__icon"
      name="calendar"
      :width="iconSize"
      color="#979797"
      hover-color="#499259"
      cursor="pointer"
      @click.stop.prevent="toggleCalendar"
    />
    <inline-date-sections
      ref="dateGroupRef"
      :id="`${id}-date`"
      :sections="dateSections"
      separator="."
      :values="dateValues"
      :editable="editable"
      :enable-menu="showCalendar && editable"
      :move-to-body="moveToBody"
      :menu-z-index="menuZIndex"
      @update:values="onDateValues"
      @menu="onDateMenu"
      @step="onStep"
      @enter="onEnter"
    >
      <template #menu-content>
        <div
          class="inline-date__menu"
          :data-date-menu="id"
        >
          <component
            :is="calendar"
            :model-value="calendarValue"
            :min-date="minDate"
            :max-date="maxDate"
            :with-time="withTime"
            :options="calendarProps"
            @select="onCalendarSelect"
            @close="closeCalendar"
          />
        </div>
      </template>
    </inline-date-sections>
    <template v-if="withTime">
      <span class="inline-date__gap">{{ ' ' }}</span>
      <inline-date-sections
        ref="timeGroupRef"
        :id="`${id}-time`"
        :sections="timeSections"
        separator=":"
        :values="timeValues"
        :editable="editable"
        :menu-z-index="menuZIndex"
        @update:values="onTimeValues"
        @menu="onTimeMenu"
        @step="onStep"
        @enter="onEnter"
      />
    </template>
    <app-icon
      v-if="canClear"
      class="inline-date__icon inline-date__icon--clear"
      name="arrow-left"
      :width="iconSize"
      color="#979797"
      hover-color="#499259"
      cursor="pointer"
      @click.stop.prevent="clearValue"
    />
  </span>
</template>

<script lang="ts">
import { markRaw, type Component as VueComponent } from 'vue';
import { Component, Prop, Vue, Watch } from 'vue-facing-decorator';
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue';
import InlineDateSections, {
  DateSectionDef,
  DateSectionKey,
  DateSectionsMenuEvent,
  DateSectionsStepEvent,
} from '@/components/ui-kit/datepicker/InlineDateSections.vue';
import InlineDateCalendarVuepic from '@/components/ui-kit/datepicker/InlineDateCalendarVuepic.vue';

const DATE_SECTIONS: DateSectionDef[] = [
  { key: 'day', length: 2 },
  { key: 'month', length: 2 },
  { key: 'year', length: 4 },
];

const TIME_SECTIONS: DateSectionDef[] = [
  { key: 'hour', length: 2 },
  { key: 'minute', length: 2 },
];

const EMPTY_PARTS: Record<DateSectionKey, string> = {
  day: '',
  month: '',
  year: '',
  hour: '',
  minute: '',
};

/**
 * Inline date editor. Keeps the value as Date or null, owns the calendar, the limits,
 * normalization of typed digits and the move of focus between the date group and the
 * time group. Time, the frame and the label are options, and with the frame off the
 * editor is a plain inline value inside a line of text. The value is composed here and
 * not in the calendar, so typing and picking a day go through the same place and a
 * replaced calendar does not change the behaviour of the field.
 */
@Component({
  name: 'InlineDateEditor',
  components: { AppIcon, InlineDateSections },
  emits: ['update:modelValue', 'commit'],
  expose: [
    'leaveField',
    'releaseFieldFocus',
  ],
})
export default class InlineDateEditor extends Vue {
  @Prop({ default: null }) modelValue!: Date | null;
  @Prop({ type: String, required: true }) id!: string;
  // Single switch of editing. Without a frame a non editable date is plain text
  @Prop({ type: Boolean, default: true }) editable!: boolean;
  @Prop({ type: Boolean, default: false }) withTime!: boolean;
  @Prop({ type: Boolean, default: false }) bordered!: boolean;
  // Compact stands for a place where the text is 12px, like the table of parameters. The
  // icons go down with the text, and the field brings the top inset of a cell itself,
  // because a nested component dissolves through display contents and its content lands
  // in an anonymous cell, which no cell rule reaches
  @Prop({ type: Boolean, default: false }) compact!: boolean;
  @Prop({ type: String, default: '' }) label!: string;
  @Prop({ type: Number, default: 0 }) menuZIndex!: number;
  @Prop({ type: Boolean, default: true }) moveToBody!: boolean;
  // Empty limit means no limit, so nothing is substituted for it anywhere below
  @Prop({ default: undefined }) minDate!: Date | undefined;
  @Prop({ default: undefined }) maxDate!: Date | undefined;
  @Prop({ type: Boolean, default: true }) clearable!: boolean;
  @Prop({ type: Boolean, default: false }) required!: boolean;
  @Prop({ type: Boolean, default: true }) showCalendar!: boolean;
  @Prop({ type: Object, default: () => ({}) }) calendarProps!: Record<string, unknown>;
  @Prop({ default: () => markRaw(InlineDateCalendarVuepic) }) calendar!: VueComponent;

  parts: Record<DateSectionKey, string> = { ...EMPTY_PARTS };
  calendarOpen = false;

  // A reported leave waits for confirmation, because the descriptor reports leaving a
  // section before it activates the next one, and a step inside the field looks exactly
  // like an exit until that second event arrives
  private leavePending = false;

  created() {
    this.parts = this.partsFromDate(this.modelValue);
  }

  // Section sets are constants and stay out of the data of the instance, otherwise every
  // editor would share one reactive copy of the same array
  get dateSections(): DateSectionDef[] {
    return DATE_SECTIONS;
  }

  get timeSections(): DateSectionDef[] {
    return TIME_SECTIONS;
  }

  get iconSize(): string {
    return this.compact ? '14px' : '16px';
  }

  get dateValues(): Record<string, string> {
    return {
      day: this.parts.day,
      month: this.parts.month,
      year: this.parts.year,
    };
  }

  get timeValues(): Record<string, string> {
    return {
      hour: this.parts.hour,
      minute: this.parts.minute,
    };
  }

  get orderedSections(): DateSectionDef[] {
    return this.withTime
      ? [...this.dateSections, ...this.timeSections]
      : this.dateSections;
  }

  get calendarValue(): Date | null {
    return this.composeFromParts() || this.modelValue;
  }

  get canClear(): boolean {
    return this.editable && this.clearable && !this.required && this.hasAnyPart;
  }

  get hasAnyPart(): boolean {
    return this.orderedSections.some((section) => !!this.parts[section.key]);
  }

  @Watch('modelValue')
  onModelValueChanged(value: Date | null) {
    if (!this.visiblePartsChanged) {
      return;
    }
    this.parts = this.partsFromDate(value);
  }

  onDateValues(values: Record<string, string>) {
    this.parts = { ...this.parts, ...values } as Record<DateSectionKey, string>;
  }

  onTimeValues(values: Record<string, string>) {
    this.parts = { ...this.parts, ...values } as Record<DateSectionKey, string>;
  }

  onDateMenu(event: DateSectionsMenuEvent) {
    this.handleMenu(event, true);
  }

  onTimeMenu(event: DateSectionsMenuEvent) {
    this.handleMenu(event, false);
  }

  private handleMenu(event: DateSectionsMenuEvent, fromDateGroup: boolean) {
    if (event.eventKind === 1) {
      this.onSectionLeft();
      return;
    }

    // Any other event means a section of this field is live, so the leave reported just
    // before it was a step inside the field
    this.leavePending = false;

    if (fromDateGroup) {
      this.openCalendar();
    } else {
      this.closeCalendar();
    }

    if (event.eventKind === 0) {
      this.advanceWhenFilled(event.key, event.text);
    }
  }

  // Blur of a section means either a step to a neighbour section or leaving the whole
  // field. While the caret stays in the field the typed text is left alone, padding and
  // limits belong to the exit, and so does dropping a date that was never finished
  private onSectionLeft() {
    this.leavePending = true;
    setTimeout(() => {
      if (!this.leavePending) {
        return;
      }
      this.leavePending = false;
      if (this.isFocusInsideField()) {
        return;
      }
      this.commitAndClose();
    }, 0);
  }

  // The field owns its whole box, the sections together with the icons, the gap between
  // the groups and the frame. A press inside the box is not leaving the field, so it
  // keeps the caret where it was instead of letting the press take the focus away
  private onFieldMouseDown(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    // The menu keeps its own focus, and with moveToBody off it lies inside the field
    if (target?.closest('.section, .fmenu')) {
      return;
    }
    event.preventDefault();
  }

  onEnter() {
    this.releaseFieldFocus();
  }

  private isFocusInsideField(): boolean {
    const active = document.activeElement as HTMLElement | null;
    if (!active) {
      return false;
    }
    const root = this.$refs.rootRef as HTMLElement | undefined;
    if (root && root.contains(active)) {
      return true;
    }
    // The menu is teleported to body, so containment by the root says nothing about it
    const menu = active.closest?.('.fmenu');
    return !!menu && !!menu.querySelector(`[data-date-menu="${this.id}"]`);
  }

  private commitAndClose() {
    this.normalizeParts();
    // A half typed date is not a value, so it is dropped instead of being guessed
    if (!this.hasFullDate) {
      this.parts = { ...EMPTY_PARTS };
    } else {
      // Limits can pull the value, and the sections have to show what was really taken
      const value = this.composeFromParts();
      if (value) {
        this.parts = this.partsFromDate(value);
      }
    }
    this.commitValue();
    // Sections show the value that was really taken, together with the padding and the
    // limits, even when the committed value matches the one the field already had
    this.applyGroups();
    this.closeCalendar();
  }

  // The owning descriptor closes a live field by walking its children, and the field is
  // the one to decide whether it is the live one
  leaveField(): boolean {
    if (!this.isFieldLive) {
      return false;
    }
    this.releaseFieldFocus();
    this.commitAndClose();
    return true;
  }

  releaseFieldFocus() {
    const active = document.activeElement as HTMLElement | null;
    const root = this.$refs.rootRef as HTMLElement | undefined;
    if (active && root && root.contains(active)) {
      active.blur();
    }
  }

  // Live means the caret is in one of the sections or in the calendar. The open flag is
  // not asked here, because the descriptor can close the menu on its own and never say so
  get isFieldLive(): boolean {
    return this.isFocusInsideField();
  }

  get hasFullDate(): boolean {
    return !!this.parts.day && !!this.parts.month && !!this.parts.year;
  }

  get visiblePartsChanged(): boolean {
    const stored = this.partsFromDate(this.modelValue);
    return this.orderedSections.some(
      (section) => (this.parts[section.key] || '') !== (stored[section.key] || '')
    );
  }

  private advanceWhenFilled(key: string | null, text: string) {
    if (!key) {
      return;
    }
    const index = this.orderedSections.findIndex((section) => section.key === key);
    if (index < 0 || text.length < this.orderedSections[index].length) {
      return;
    }
    const next = this.orderedSections[index + 1];
    if (!next) {
      return;
    }
    this.$nextTick(() => {
      this.groupOf(next.key)?.activateSection(next.key);
    });
  }

  onStep(event: DateSectionsStepEvent) {
    const key = event.key as DateSectionKey;
    const section = this.orderedSections.find((item) => item.key === key);
    if (!section || !this.editable) {
      return;
    }
    const range = this.rangeOf(key);
    const current = this.parts[key] ? parseInt(this.parts[key], 10) : this.todayValue(key);
    const next = Math.min(range.max, Math.max(range.min, current + event.delta));
    this.parts = {
      ...this.parts,
      [key]: String(next).padStart(section.length, '0'),
    } as Record<DateSectionKey, string>;
  }

  onCalendarSelect(value: Date | null) {
    this.parts = this.partsFromDate(value);
    this.commitValue();
  }

  clearValue() {
    this.parts = { ...EMPTY_PARTS };
    this.applyGroups();
    this.commitValue();
    this.closeCalendar();
  }

  toggleCalendar() {
    if (this.calendarOpen) {
      this.closeCalendar();
      return;
    }
    this.openCalendar();
  }

  private openCalendar() {
    if (!this.showCalendar || !this.editable) {
      return;
    }
    const group = this.$refs.dateGroupRef as any;
    group?.openMenu?.((this.$refs.rootRef as HTMLElement) || null);
    this.calendarOpen = true;
  }

  private closeCalendar() {
    const group = this.$refs.dateGroupRef as any;
    group?.hideMenu?.();
    this.calendarOpen = false;
  }

  private groupOf(key: DateSectionKey): any {
    const isTimeKey = key === 'hour' || key === 'minute';
    return isTimeKey ? this.$refs.timeGroupRef : this.$refs.dateGroupRef;
  }

  private applyGroups() {
    this.$nextTick(() => {
      (this.$refs.dateGroupRef as any)?.applyModel?.();
      (this.$refs.timeGroupRef as any)?.applyModel?.();
    });
  }

  private commitValue() {
    // Compared by visible sections, not by the moment in time. A stored date can carry
    // any time of day, and entering the field must not rewrite it with midnight
    if (!this.visiblePartsChanged) {
      return;
    }
    const value = this.composeFromParts();
    // A required field keeps its value, an emptied one goes back to what it was
    if (!value && this.required && this.modelValue) {
      this.parts = this.partsFromDate(this.modelValue);
      this.applyGroups();
      return;
    }
    this.$emit('update:modelValue', value);
    this.$emit('commit', value);
  }

  // Normalization runs when a section is left, not on every character, otherwise it
  // fights the caret while the user is still typing
  private normalizeParts() {
    const next = { ...this.parts };
    next.year = this.normalizeYear(next.year);
    next.month = this.padInRange(next.month, 2, 1, 12);
    next.hour = this.padInRange(next.hour, 2, 0, 23);
    next.minute = this.padInRange(next.minute, 2, 0, 59);

    const year = next.year ? parseInt(next.year, 10) : new Date().getFullYear();
    const month = next.month ? parseInt(next.month, 10) : 1;
    next.day = this.padInRange(next.day, 2, 1, this.daysInMonth(year, month));

    this.parts = next;
  }

  private normalizeYear(raw: string): string {
    if (!raw) {
      return '';
    }
    const digits = raw.slice(0, 4);
    const value = parseInt(digits, 10);
    if (!Number.isFinite(value)) {
      return '';
    }
    // Two digits are read as the current century, this is how short years are typed
    const full = digits.length < 3 ? 2000 + value : value;
    return String(full).padStart(4, '0');
  }

  private padInRange(raw: string, length: number, min: number, max: number): string {
    if (!raw) {
      return '';
    }
    const value = parseInt(raw.slice(0, length), 10);
    if (!Number.isFinite(value)) {
      return '';
    }
    return String(Math.min(max, Math.max(min, value))).padStart(length, '0');
  }

  private rangeOf(key: DateSectionKey): { min: number; max: number } {
    switch (key) {
      case 'month':
        return { min: 1, max: 12 };
      case 'year':
        return { min: 1, max: 9999 };
      case 'hour':
        return { min: 0, max: 23 };
      case 'minute':
        return { min: 0, max: 59 };
      default: {
        const year = this.parts.year ? parseInt(this.parts.year, 10) : new Date().getFullYear();
        const month = this.parts.month ? parseInt(this.parts.month, 10) : 1;
        return { min: 1, max: this.daysInMonth(year, month) };
      }
    }
  }

  private todayValue(key: DateSectionKey): number {
    const today = new Date();
    switch (key) {
      case 'day':
        return today.getDate();
      case 'month':
        return today.getMonth() + 1;
      case 'year':
        return today.getFullYear();
      default:
        return 0;
    }
  }

  private daysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
  }

  private partsFromDate(value: Date | null): Record<DateSectionKey, string> {
    if (!value) {
      return { ...EMPTY_PARTS };
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return { ...EMPTY_PARTS };
    }
    return {
      day: String(date.getDate()).padStart(2, '0'),
      month: String(date.getMonth() + 1).padStart(2, '0'),
      year: String(date.getFullYear()).padStart(4, '0'),
      hour: String(date.getHours()).padStart(2, '0'),
      minute: String(date.getMinutes()).padStart(2, '0'),
    };
  }

  // The value is put together here and not in the calendar, so that typing and picking
  // a day go through the same place
  private composeFromParts(): Date | null {
    if (!this.hasFullDate) {
      return null;
    }
    const year = parseInt(this.parts.year, 10);
    const month = parseInt(this.parts.month, 10);
    const day = parseInt(this.parts.day, 10);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return null;
    }
    const hour = this.withTime && this.parts.hour ? parseInt(this.parts.hour, 10) : 0;
    const minute = this.withTime && this.parts.minute ? parseInt(this.parts.minute, 10) : 0;
    const composed = new Date(year, month - 1, day, hour, minute, 0, 0);
    return this.clampToLimits(composed);
  }

  private clampToLimits(value: Date): Date {
    if (this.minDate && value.getTime() < new Date(this.minDate).getTime()) {
      return new Date(this.minDate);
    }
    if (this.maxDate && value.getTime() > new Date(this.maxDate).getTime()) {
      return new Date(this.maxDate);
    }
    return value;
  }
}
</script>

<style scoped lang="scss">
.inline-date {
  display: inline;
  font-variant-numeric: tabular-nums;
  white-space: normal;
}

.inline-date__icon {
  position: relative;
  top: -1px;
  margin-right: 4px;
}

.inline-date__icon--clear {
  margin-left: 4px;
  margin-right: 0;
}

.inline-date__menu {
  padding: 4px;
}

.inline-date--compact {
  display: inline-block;
  padding-top: 8px;
}

.inline-date--bordered {
  display: inline-block;
  box-sizing: border-box;
  position: relative;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #d2d2d2;
  border-radius: 8px;
  font-size: 14px;
  line-height: 30px;
  background: #fff;
}

.inline-date--bordered:hover {
  border-color: #3f884f;
}

.inline-date--bordered:focus-within {
  border-color: #3f884f;
}

.inline-date__label {
  position: absolute;
  top: -8px;
  left: 8px;
  padding: 0 4px;
  background: #fff;
  color: #979797;
  font-size: 12px;
  line-height: 16px;
}
</style>
